import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import {
  isTenantSettingFlagEnabled,
  TENANT_SETTING_ENABLE_ONLINE_SIGNUP_CHECKOUT_SECURITY,
} from '../constants/checkout-security.constants';
import { CONST_URL_SEGEMENTS } from 'paylink-constants';
import { WIDGETS_B2B_API_URL } from '../tokens/widgets-b2b-api-url.token';
import { buildWidgetApiPath } from '../utils/widget-api-path.util';

interface TenantSettingByKey {
  value?: string;
  status?: string;
}

@Injectable({ providedIn: 'root' })
export class WidgetCheckoutSecurityService {
  private readonly http = inject(HttpClient);
  private readonly b2bApiUrl = inject(WIDGETS_B2B_API_URL);
  private readonly cache = new Map<string, Observable<boolean>>();

  isCheckoutSecurityEnabled(
    companyUuid: string,
    tenantUuid: string,
  ): Observable<boolean> {
    if (!companyUuid?.trim() || !tenantUuid?.trim()) {
      return of(false);
    }

    const cacheKey = `${companyUuid}:${tenantUuid}`;
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const path = buildWidgetApiPath(
      CONST_URL_SEGEMENTS.OPEN_API.GET_TENANT_SETTING_BY_KEY,
      {
        company_uuid: companyUuid,
        tenant_uuid: tenantUuid,
        tentstg_key: TENANT_SETTING_ENABLE_ONLINE_SIGNUP_CHECKOUT_SECURITY,
      },
    );
    const url = `${this.b2bApiUrl}${path}`;

    const request$ = this.http.get<TenantSettingByKey>(url).pipe(
      map((setting) => isTenantSettingFlagEnabled(setting)),
      catchError((error: unknown) => {
        // 409 = setting/tenant missing on B2B API — treat as disabled (same as checkout off).
        if (error instanceof HttpErrorResponse && error.status === 409) {
          return of(false);
        }
        return of(false);
      }),
      shareReplay(1),
    );

    this.cache.set(cacheKey, request$);
    return request$;
  }

  clearCache(): void {
    this.cache.clear();
  }
}
